/**
 * Shared internals of the loyalty-card camera scanner.
 *
 * Extracted from CounterScanLoyaltyCard.tsx so the staff-phone /scan surface
 * and the till's guest popover drive the exact same viewfinder, decoder and
 * error copy — one camera stack, two skins. Nothing here knows about orders
 * or attach flows; callers get a decoded token string and take it from there.
 */
import { useEffect, useRef, useState } from 'react'
import { Loader2, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  cameraLabel,
  clearScannerCamera,
  getScannerCamera,
  listVideoInputs,
  setScannerCameraId,
} from '@/lib/scannerCamera'
import { isBhooklyNativeServerApp } from '@/lib/bhooklyNative'

/**
 * Live camera QR scanner for a customer's wallet card.
 *
 * Decoding uses the browser's native BarcodeDetector where it exists and a
 * lazily-imported jsQR everywhere else. That fallback is not optional: Chrome
 * only ships BarcodeDetector on Android, macOS and ChromeOS — on Chrome for
 * WINDOWS (and Linux), and therefore inside the Electron shell on every
 * restaurant PC we deploy to, the API is simply undefined. Gating the feature
 * on it hid camera scanning from the entire till fleet while it worked fine on
 * the developer's Mac. Support is now gated on getUserMedia alone.
 *
 * The camera choice is remembered per machine (lib/scannerCamera.ts): a Surface
 * till has a front and a rear camera, and the rear one is what lets a customer
 * hold their phone against the back of the machine to be punched in.
 */
export function CameraCardScanner({
  onDecode,
  onClose,
  busy,
  decoderFactory = createDecoder,
  hint = "Hold the customer's wallet card in the frame.",
  frame = 'square',
}: {
  onDecode: (token: string) => void
  onClose: () => void
  busy: boolean
  /** The store's barcode scanner passes createBarcodeDecoder (scannerCamera.ts). */
  decoderFactory?: () => Promise<Decode>
  hint?: string
  /** 'wide' draws a letterbox guide for a linear barcode instead of a QR square. */
  frame?: 'square' | 'wide'
}) {
  const videoRef = useRef<HTMLVideoElement | null>(null)
  const [error, setError] = useState<string | null>(null)
  // '' => let the browser choose (rear-facing if it can). A saved id is only a
  // hint: cameras get unplugged and ids rotate, so a dead one must not brick
  // the scanner — see the OverconstrainedError retry below.
  const [activeId, setActiveId] = useState(() => getScannerCamera().deviceId)
  const [devices, setDevices] = useState<MediaDeviceInfo[]>([])
  // Ref, not state: the rAF loop closes over it and must see the latest value
  // without re-subscribing, and a second decode of the same frame would fire
  // a duplicate attach.
  const doneRef = useRef(false)
  // The parent rebuilds onDecode on every render (it closes over a mutation).
  // Depending on it directly would tear down and restart the camera on each
  // one, so hold it in a ref and start the stream exactly once.
  const onDecodeRef = useRef(onDecode)
  onDecodeRef.current = onDecode

  useEffect(() => {
    let stream: MediaStream | null = null
    let timer: ReturnType<typeof setTimeout> | undefined
    let cancelled = false

    async function open(deviceId: string): Promise<MediaStream> {
      return navigator.mediaDevices.getUserMedia({
        video: deviceId
          ? { deviceId: { exact: deviceId } }
          : // Rear camera when the device knows which way it faces; ignored on
            // a laptop with a single webcam.
            { facingMode: { ideal: 'environment' } },
        audio: false,
      })
    }

    async function start() {
      try {
        setError(null)
        try {
          stream = await open(activeId)
        } catch (err) {
          const name = (err as { name?: string })?.name
          // The pinned camera is gone (unplugged dock, cleared storage rotated
          // the ids). Forget it and fall back rather than staying dead.
          if (activeId && (name === 'OverconstrainedError' || name === 'NotFoundError')) {
            clearScannerCamera()
            if (cancelled) return
            setActiveId('')
            return
          }
          throw err
        }
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop())
          return
        }
        const video = videoRef.current
        if (!video) return
        video.srcObject = stream
        await video.play()

        // Labels are blank until permission has been granted, so enumerate only
        // now that the stream is live and the picker can show real names.
        void listVideoInputs().then((found) => {
          if (!cancelled) setDevices(found)
        })

        const decode = await decoderFactory()
        if (cancelled) return

        const tick = async () => {
          if (cancelled || doneRef.current) return
          try {
            const value = await decode(video)
            if (value) {
              doneRef.current = true
              onDecodeRef.current(value)
              return
            }
          } catch {
            // A transient decode failure (e.g. a frame arriving mid-resize)
            // is normal — keep scanning rather than tearing the view down.
          }
          // Throttled rather than per-animation-frame: the jsQR fallback runs
          // on the main thread and 60fps of it would peg a weak till CPU.
          // 10fps is plenty for a card held up to the lens.
          timer = setTimeout(() => void tick(), DECODE_INTERVAL_MS)
        }
        timer = setTimeout(() => void tick(), DECODE_INTERVAL_MS)
      } catch (err) {
        if (cancelled) return
        setError(describeCameraError(err))
      }
    }
    void start()

    return () => {
      cancelled = true
      if (timer) clearTimeout(timer)
      // Release the camera, or the indicator light stays on and the next
      // open fails with NotReadableError on some Android builds.
      stream?.getTracks().forEach((t) => t.stop())
    }
    // Restarts when the cashier picks a different camera. onDecode is read
    // through a ref (above) so it never restarts the stream on a re-render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeId])

  return (
    <div className="space-y-1.5">
      {devices.length > 1 && (
        <label className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
          <span className="shrink-0">Camera</span>
          <select
            className="h-7 min-w-0 flex-1 rounded-md border border-input bg-background px-1.5 text-[11px]"
            value={activeId}
            onChange={(e) => {
              const id = e.target.value
              setScannerCameraId(id)
              doneRef.current = false
              setActiveId(id)
            }}
          >
            <option value="">Automatic</option>
            {devices.map((d, i) => (
              <option key={d.deviceId} value={d.deviceId}>
                {cameraLabel(d, i)}
              </option>
            ))}
          </select>
        </label>
      )}
      <div className="relative overflow-hidden rounded-md bg-black">
        <video
          ref={videoRef}
          className="aspect-[4/3] w-full object-cover"
          muted
          playsInline
          autoPlay
        />
        {/* Framing guide — staff aim the customer's card inside it. */}
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <div className={frame === 'wide' ? 'h-24 w-4/5 rounded-lg border-2 border-white/70' : 'h-32 w-32 rounded-lg border-2 border-white/70'} />
        </div>
        {busy && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/50">
            <Loader2 className="h-6 w-6 animate-spin text-white" />
          </div>
        )}
        <Button
          type="button"
          size="sm"
          variant="secondary"
          className="absolute right-1.5 top-1.5 h-7 w-7 px-0"
          onClick={onClose}
          aria-label="Close camera"
        >
          <X className="h-3.5 w-3.5" />
        </Button>
      </div>
      <p className="text-[11px] leading-snug text-muted-foreground">
        {error ?? hint}
      </p>
    </div>
  )
}

/** Minimal shape of the BarcodeDetector API, which lib.dom doesn't declare. */
interface BarcodeDetectorCtor {
  new (options?: { formats?: string[] }): {
    detect(source: CanvasImageSource): Promise<{ rawValue?: string }[]>
  }
  getSupportedFormats?: () => Promise<string[]>
}

/** How often we decode a frame. 10fps — see the comment at the call site. */
const DECODE_INTERVAL_MS = 100

/** Long edge, in px, the frame is downscaled to before jsQR sees it. */
const MAX_DECODE_EDGE = 480

/** Decodes the QR currently in front of the camera, or null if there isn't one. */
export type Decode = (video: HTMLVideoElement) => Promise<string | null>

/**
 * Picks a QR decoder for this browser.
 *
 * Native BarcodeDetector when it exists AND actually advertises qr_code (some
 * builds expose the constructor with an empty format list). Otherwise jsQR,
 * dynamically imported so the ~40KB never lands in the main bundle on the
 * devices that don't need it.
 */
export async function createDecoder(): Promise<Decode> {
  const native = (window as unknown as { BarcodeDetector?: BarcodeDetectorCtor }).BarcodeDetector
  if (native) {
    try {
      const formats = (await native.getSupportedFormats?.()) ?? ['qr_code']
      if (formats.includes('qr_code')) {
        const detector = new native({ formats: ['qr_code'] })
        return async (video) => {
          const found = await detector.detect(video)
          return found.find((b) => b.rawValue?.trim())?.rawValue?.trim() ?? null
        }
      }
    } catch {
      // Constructing/probing the native detector failed — fall through to jsQR.
    }
  }

  const { default: jsQR } = await import('jsqr')
  const canvas = document.createElement('canvas')
  const ctx = canvas.getContext('2d', { willReadFrequently: true })
  return async (video) => {
    const vw = video.videoWidth
    const vh = video.videoHeight
    if (!ctx || !vw || !vh) return null
    // Decoding a full 1080p frame is pointlessly expensive; a wallet QR held up
    // to the lens survives the downscale easily.
    const scale = Math.min(1, MAX_DECODE_EDGE / Math.max(vw, vh))
    const w = Math.max(1, Math.round(vw * scale))
    const h = Math.max(1, Math.round(vh * scale))
    if (canvas.width !== w || canvas.height !== h) {
      canvas.width = w
      canvas.height = h
    }
    ctx.drawImage(video, 0, 0, w, h)
    const { data } = ctx.getImageData(0, 0, w, h)
    const result = jsQR(data, w, h, { inversionAttempts: 'dontInvert' })
    return result?.data?.trim() || null
  }
}

export function describeCameraError(err: unknown): string {
  const name = (err as { name?: string })?.name
  if (name === 'NotAllowedError' || name === 'SecurityError') {
    // The remedy is per-platform and naming the wrong one is worse than
    // saying nothing — a server on a floor tablet has no "Windows till"
    // settings to open, and would read that copy as "this is broken".
    if (isBhooklyNativeServerApp()) {
      return 'Camera blocked. Settings → Apps → Bhookly Android → Permissions → Camera → Allow. Or type the code.'
    }
    // Most tills are the Windows desktop app, where "browser settings" means
    // nothing — the real blocker there is the OS camera privacy toggle.
    return 'Camera blocked. On a Windows till: Settings → Privacy & security → Camera → allow desktop apps. Or type the code.'
  }
  if (name === 'NotFoundError' || name === 'OverconstrainedError') {
    return 'No camera found on this device — use the scanner or type the code.'
  }
  if (name === 'NotReadableError') {
    return 'The camera is in use by another app. Close it and try again.'
  }
  return 'Could not start the camera. Use the scanner or type the code.'
}

/** Tagged error so onError can map a known reason to friendly copy. */
export class ScanError extends Error {
  reason: string
  constructor(reason: string, message: string) {
    super(message)
    this.reason = reason
  }
}

export function describeScanError(err: unknown): { title: string; description: string } {
  if (err instanceof ScanError) {
    return { title: 'Could not attach card', description: err.message }
  }
  // Server 4xx — apiClient throws with response.data.error.
  const reason =
    err && typeof err === 'object'
      ? (err as { response?: { data?: { error?: string } } }).response?.data?.error
      : undefined
  if (reason === 'card_not_found') {
    return { title: 'Card not recognised', description: 'No customer matches that card code.' }
  }
  return {
    title: 'Scan failed',
    description: err instanceof Error ? err.message : 'Try scanning again.',
  }
}
